<script>
import { BFormRadio, BFormRadioGroup } from 'bootstrap-vue'
import MaterialFirstFMDEditor from "@/views/product/components/product-card/fmd/MaterialFirstFMDEditor.vue";
import ComponentFirstFMDEditor from "@/views/product/components/product-card/fmd/ComponentFirstFMDEditor.vue";
import {SubstanceApi} from "@core/api";
import SubstanceListsCacheManager from "@/views/product/components/product-card/fmd/SubstanceListsCacheManager";

export default {
    components: {
        BFormRadio,
        BFormRadioGroup,
        MaterialFirstFMDEditor,
        ComponentFirstFMDEditor,
    },
    props: {
        productId: {
            type: [String, Number],
            required: true
        },
        getSubstanceLists: {
            type: Function,
            default: null,
        },
        cacheSubstanceLists: {
            type: Boolean,
            default: true,
        },
        maxListsInCell: {
            type: Number,
            default: 0,
        },
        showModeSelector: {
            type: Boolean,
            default: true,
        },
        defaultEditorMode: {
            type: String,
            default: 'component-first',
            validator(value) {
                return ['material-first', 'component-first'].includes(value)
            },
        },
        declarations: {
            type: Array,
            default() {
                return [];
            },
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        requireSubstance: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editorMode: this.defaultEditorMode,
            substanceListsCache: {},
            substanceListsState: {},
            substanceListsManager: null,
        }
    },
    computed: {
        resolvedGetSubstanceLists() {
            return this.getSubstanceLists || SubstanceApi.getSubstanceLists
        },
    },
    watch: {
        defaultEditorMode(next) {
            this.editorMode = next
        },
        cacheSubstanceLists(next) {
            if (this.substanceListsManager) {
                this.substanceListsManager.updateConfig({ cacheEnabled: next })
            }
        },
        getSubstanceLists() {
            if (this.substanceListsManager) {
                this.substanceListsManager.updateConfig({ fetcher: this.resolvedGetSubstanceLists })
            }
        },
    },
    created() {
        this.substanceListsManager = new SubstanceListsCacheManager({
            fetcher: this.resolvedGetSubstanceLists,
            ttlMs: 15 * 60 * 1000,
            cacheEnabled: this.cacheSubstanceLists,
            stateRef: this.substanceListsState,
            cacheRef: this.substanceListsCache,
            setState: (id, state) => this.$set(this.substanceListsState, id, state),
            setCache: (id, entry) => this.$set(this.substanceListsCache, id, entry),
        })
    },
    methods: {
        ensureSubstanceLists(substanceId) {
            if (!this.substanceListsManager) {
                return Promise.resolve(null)
            }
            return this.substanceListsManager.ensure(substanceId)
        },
        getSubstanceListsText(substanceId) {
            if (!substanceId) {
                return '-'
            }
            const state = this.substanceListsState[substanceId]
            if (!state) {
                return '-'
            }
            if (state.loading) {
                return '...'
            }
            if (state.error) {
                return '-'
            }
            const lists = Array.isArray(state.lists) ? state.lists : []
            if (!lists.length) {
                return '-'
            }
            const titles = lists.map(item => (item && item.title ? item.title : '')).filter(Boolean)
            if (!titles.length) {
                return '-'
            }
            const limit = this.maxListsInCell
            const hasLimit = Number.isFinite(limit) && limit > 0
            const visible = hasLimit ? titles.slice(0, limit) : titles
            const text = visible.join(', ')
            return text || '-'
        },
        getSubstanceListsExtra(substanceId) {
            if (!substanceId) {
                return 0
            }
            const state = this.substanceListsState[substanceId]
            if (!state || state.loading || state.error) {
                return 0
            }
            const lists = Array.isArray(state.lists) ? state.lists : []
            if (!lists.length) {
                return 0
            }
            const titles = lists.map(item => (item && item.title ? item.title : '')).filter(Boolean)
            if (!titles.length) {
                return 0
            }
            const limit = this.maxListsInCell
            const hasLimit = Number.isFinite(limit) && limit > 0
            if (!hasLimit) {
                return 0
            }
            const remaining = titles.length - Math.min(limit, titles.length)
            return remaining > 0 ? remaining : 0
        },
    },
}
</script>

<template>
    <div>
        <b-form-radio-group
            v-if="showModeSelector"
            v-model="editorMode"
            class="mb-1"
        >
            <b-form-radio value="material-first">
                {{ $t('Component(FMDEditor):mode-material-first') }}
            </b-form-radio>
            <b-form-radio value="component-first">
                {{ $t('Component(FMDEditor):mode-component-first') }}
            </b-form-radio>
        </b-form-radio-group>

        <MaterialFirstFMDEditor
            v-if="editorMode === 'material-first'"
            :initial-version="1"
            :product-id="productId"
            :initial-declarations="declarations"
            :readonly="readOnly"
            :require-substance="requireSubstance"
            :ensure-substance-lists-fn="ensureSubstanceLists"
            :get-substance-lists-text-fn="getSubstanceListsText"
            :get-substance-lists-extra-fn="getSubstanceListsExtra"
            @change="$emit('change', $event)"
            @editing-change="$emit('editing-change', $event)"
            @substance-removed="$emit('substance-removed', $event)"
        />
        <ComponentFirstFMDEditor
            v-else
            :initial-version="1"
            :product-id="productId"
            :initial-declarations="declarations"
            :readonly="readOnly"
            :require-substance="requireSubstance"
            :ensure-substance-lists-fn="ensureSubstanceLists"
            :get-substance-lists-text-fn="getSubstanceListsText"
            :get-substance-lists-extra-fn="getSubstanceListsExtra"
            @change="$emit('change', $event)"
            @editing-change="$emit('editing-change', $event)"
            @substance-removed="$emit('substance-removed', $event)"
        />
    </div>
</template>

<style scoped lang="scss">

</style>
